import { Controller, useForm } from "react-hook-form";
import { Dialog } from "components/Dialog";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { TRole, UserType } from "types/dto";
import { Select } from "components/Select";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { MultipleSelect } from "components/MultipleSelect";

import { netCall } from "utils/netCall";
import { UserManagementApiUrl } from "environment/routeSettings";
import { Icon } from "components/Icon";
import { TResponseCode } from "utils/netCall/types";
import { useRegion_v2 } from "contexts/regions_v2";

type TProps = {
  isOpen?: boolean;
  onClose: () => void;
  onSubmit: (value: TAddFormTypes) => void;
  isLoading: boolean;
};

export type TAddFormTypes = {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  provinceId: string;
  cityId: string;
  jurisdictionId: string;
  userType: UserType;
  roles: string[];
  password: string;
};

const userTypeOptions = SortObjectByPropName(Enum2Array(UserType), "name");

export function AddUseModal(props: TProps) {
  const { isOpen = false, onClose, onSubmit, isLoading } = props;

  const { t } = useTranslation();
  const { selectedRegion } = useRegion_v2();

  const [rolesData, setRolesData] = useState<TRole[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
  } = useForm<TAddFormTypes>({
    defaultValues: undefined,
  });

  const getRoles = async () => {
    const res = await netCall<TRole[]>({
      method: "GET",
      endPoint: `${UserManagementApiUrl()}/api/SamaRole/GetRoles`,
    });
    if (res.responseCode === TResponseCode.OK) {
      const records = res.results;
      setRolesData(records);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  return (
    <>
      <Dialog
        maxWidth={false}
        dialogTitle={t("addUser")}
        open={isOpen}
        onClose={onClose}
        canClose={!isLoading}
      >
        <form className="p-4 w-[640px]" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col w-full gap-y-3 px-2">
            <p className={"font-bold text-primary-80"}>{t("userInfo")}</p>
            <div className={"grid grid-cols-3 gap-x-2 gap-y-3"}>
              <Controller
                name={"firstName"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("firstName")}
                      placeholder={t("firstName")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"lastName"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("lastName")}
                      placeholder={t("lastName")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"email"}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Please enter a valid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("email")}
                      placeholder={t("email")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
            </div>
            <p className={"font-bold text-primary-80 mt-3"}>
              {t("userAccountInfo")}
            </p>
            <div className={"grid grid-cols-2 gap-x-2 px-2"}>
              <Controller
                name={"username"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("username")}
                      placeholder={t("username")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"password"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("password")}
                      placeholder={t("password")}
                      type={showPassword ? "text" : "password"}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setShowPassword((prev) => !prev);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {!showPassword ? (
                                <Icon
                                  className={"text-gray-90"}
                                  icon={"MdVisibilityOff"}
                                  fontSize={"18px"}
                                />
                              ) : (
                                <Icon
                                  icon={"MdVisibility"}
                                  className={"text-gray-90"}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />
            </div>
            <p className={"font-bold text-primary-80 mt-3"}>{t("roleInfo")}</p>
            <div className={"grid grid-cols-3 gap-x-2 px-2 gap-y-3"}>
              <div className={"col-span-3"}>
                <RegionAutoComplete
                  onChange={(value) => {
                    setValue("provinceId", value?.province?.id || "");
                    setValue("cityId", value?.city?.id || "");
                    setValue("jurisdictionId", value?.jurisdiction?.id || "");
                  }}
                  defaultValue={{
                    provinceId: selectedRegion.province?.id,
                    cityId: selectedRegion.city?.id,
                    jurisdictionId: selectedRegion.jurisdiction?.id,
                  }}
                />
              </div>
              <Controller
                name={"userType"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Select
                      size="small"
                      className={"col-span-1"}
                      label={t("userType")}
                      options={userTypeOptions}
                      onChange={(e) => {
                        field.onChange(e?.value);
                      }}
                      value={field.value}
                      isError={Boolean(error)}
                    />
                  );
                }}
              />
              <Controller
                name={"roles"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <MultipleSelect
                      size="small"
                      label={t("roles")}
                      className="col-span-2"
                      options={rolesData.map((role) => ({
                        name: role.name,
                        value: role.id,
                      }))}
                      isError={!!error}
                      onChange={(selectedOptions) => {
                        field.onChange(selectedOptions.map((opt) => opt.value));
                      }}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="col-span-12 flex items-center justify-end mt-6">
            <Button
              className="w-20"
              loading={isLoading}
              type="submit"
              variant="contained"
            >
              {t("add")}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
